import { useContext } from 'react';

import { Box } from '@zillow/constellation-10';
import { ErrorPage as ErrorPageComponent } from '@zg-rentals/visual-cohesion';

import { RMWContext } from '../../providers/RMWContext';
import { RoutingSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import ErrorMessageConstants, { ERROR_PAGE_TYPES } from '../../constants/ErrorMessageConstants';

import HeadWrapper from '../../components/HeadWrapper';

const SUPPORT_EMAIL = 'mailto:rentalsupport@zillow.com';
const ZRM_HOME = 'https://www.zillow.com/rental-manager';

const ERROR_CONFIG = {
  [ERROR_PAGE_TYPES.NOT_FOUND]: {
    title: 'Page not found',
    message: 'We can’t find that page',
    details: 'That page may have moved or no longer exists. Let’s get you back on track.',
    action: <a href={ZRM_HOME}>Go to Rental Manager</a>,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.TECHNICAL_DIFFICULTIES]: {
    title: 'Technical difficulties',
    message: 'Something went wrong on our end',
    details: ErrorMessageConstants.TECHNICAL_DIFFICULTIES,
    action: <a href={ZRM_HOME}>Go to Rental Manager</a>,
    secondaryAction: <a href={SUPPORT_EMAIL}>Contact support</a>,
  },
  [ERROR_PAGE_TYPES.UNAUTHORIZED]: {
    title: 'Unauthorized',
    message: 'Something went wrong',
    details: ErrorMessageConstants.LISTING_NO_PERMISSION,
    action: <a href={ZRM_HOME}>Go to Rental Manager</a>,
    secondaryAction: undefined,
  },
  [ERROR_PAGE_TYPES.PREMIUM_ANALYTICS_ERROR]: {
    title: 'Technical difficulties',
    message: 'Something went wrong',
    details: 'We are having trouble loading your data. Try reloading the page or come back later.',
    action: null,
    secondaryAction: undefined,
  },
};

const ErrorPage = ({
  type = ERROR_PAGE_TYPES.NOT_FOUND,
}: {
  type?: (typeof ERROR_PAGE_TYPES)[keyof typeof ERROR_PAGE_TYPES];
}) => {
  const { paths } = useContext(RMWContext);
  const listingId = useSelector((state) => RoutingSelectors.makeListingId(state));

  const { title, message, details, action, secondaryAction } = ERROR_CONFIG[type];

  const resolvedAction =
    type === ERROR_PAGE_TYPES.PREMIUM_ANALYTICS_ERROR ? (
      <a href={`${paths.propertiesPage}/${listingId}/listing`}>Return to your property</a>
    ) : (
      action
    );

  return (
    <>
      <HeadWrapper title={title} />
      <Box css={{ height: 'calc(100dvh - 56px - 48px)' }}>
        <ErrorPageComponent
          errorMessage={message}
          errorDetails={details}
          action={resolvedAction ?? undefined}
          secondaryAction={secondaryAction}
        />
      </Box>
    </>
  );
};

export default ErrorPage;
